import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Download, Share2, BookOpen, Scale, Gavel } from 'lucide-react';
import { CaseData } from '../../types';
import { useTheme } from './ThemeContext';

const Card: React.FC<CaseData> = ({
    title,
    description,
    date,
    link,
    caseno,
    casename,
    court,
    casestatus,
    judge,
    sect,
    facts,
    petition,
    legalissues,
    keylegalques,
    plaintiffarguments,
    defendantarguments,
    courtsreasoning,
    decision,
    conclusion,
    casesummary
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { theme } = useTheme();

    const handleExpandToggle = () => {
        setIsExpanded(prev => !prev);
    };

    return (
        <div className={`rounded-lg shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border ${theme === 'dark'
                ? 'bg-[#1e2128] border-gray-700 hover:shadow-black/50'
                : 'bg-white border-gray-200'
            }`}>
            <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center">
                        <BookOpen size={20} className={`${theme === 'dark' ? 'text-blue-400' : 'text-[#302A2A]'} mr-2`} />
                        <div>
                            <h3 className={`text-lg font-semibold leading-tight ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>{title}</h3>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{court}</p>
                        </div>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${theme === 'dark'
                            ? 'bg-blue-900/30 text-blue-200'
                            : 'bg-blue-50 text-[#302A2A]'
                        }`}>{date}</span>
                </div>
                <p className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{description}</p>
                <div className="flex justify-between items-center">
                    <button
                        onClick={handleExpandToggle}
                        className={`text-sm font-medium flex items-center focus:outline-none ${theme === 'dark'
                                ? 'text-blue-400 hover:text-blue-300'
                                : 'text-[#302A2A] hover:text-blue-800'
                            }`}
                    >
                        {isExpanded ? (
                            <>
                                <ChevronUp size={16} className="mr-1" />
                                Less Details
                            </>
                        ) : (
                            <>
                                <ChevronDown size={16} className="mr-1" />
                                More Details
                            </>
                        )}
                    </button>
                    <div className="flex space-x-2">
                        <button className={`${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-[#302A2A]'} focus:outline-none`}>
                            <Download size={16} />
                        </button>
                        <button className={`${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-[#302A2A]'} focus:outline-none`}>
                            <Share2 size={16} />
                        </button>
                    </div>
                </div>
            </div>
            {isExpanded && (
                <div className={`p-4 border-t ${theme === 'dark' ? 'bg-[#16181d] border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <h4 className={`font-semibold mb-1 flex items-center ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                                <Scale size={16} className={`mr-1 ${theme === 'dark' ? 'text-blue-400' : 'text-[#302A2A]'}`} />
                                Case Details
                            </h4>
                            <div className={`p-2 rounded ${theme === 'dark' ? 'bg-[#252830] text-gray-300' : 'bg-white text-gray-800'}`}>
                                <p><span className="font-medium">Case No:</span> {caseno}</p>
                                <p><span className="font-medium">Case Name:</span> {casename}</p>
                                <p><span className="font-medium">Status:</span> {casestatus}</p>
                                <p><span className="font-medium">Judge:</span> {judge}</p>
                                <p><span className="font-medium">Section:</span> {sect}</p>
                            </div>
                        </div>
                        <div>
                            <h4 className={`font-semibold mb-1 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>Case Summary</h4>
                            <p className={`p-2 rounded ${theme === 'dark' ? 'bg-[#252830] text-gray-300' : 'bg-white text-gray-600'}`}>{casesummary}</p>
                        </div>
                    </div>
                    <div className="mt-4 space-y-3">
                        <div>
                            <h4 className={`font-semibold mb-1 flex items-center ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                                <Gavel size={16} className={`mr-1 ${theme === 'dark' ? 'text-blue-400' : 'text-[#302A2A]'}`} />
                                Legal Issues
                            </h4>
                            <p className={`p-2 rounded ${theme === 'dark' ? 'bg-[#252830] text-gray-300' : 'bg-white text-gray-600'}`}>{legalissues}</p>
                        </div>
                        {[
                            { title: 'Key Legal Questions', content: keylegalques },
                            { title: 'Facts', content: facts },
                        ].map((item, idx) => (
                            <div key={idx}>
                                <h4 className={`font-semibold mb-1 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{item.title}</h4>
                                <p className={`p-2 rounded ${theme === 'dark' ? 'bg-[#252830] text-gray-300' : 'bg-white text-gray-600'}`}>{item.content}</p>
                            </div>
                        ))}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                                { title: 'Plaintiff Arguments', content: plaintiffarguments },
                                { title: 'Defendant Arguments', content: defendantarguments },
                            ].map((item, idx) => (
                                <div key={idx}>
                                    <h4 className={`font-semibold mb-1 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{item.title}</h4>
                                    <p className={`p-2 rounded ${theme === 'dark' ? 'bg-[#252830] text-gray-300' : 'bg-white text-gray-600'}`}>{item.content}</p>
                                </div>
                            ))}
                        </div>
                        {[
                            { title: "Court's Reasoning", content: courtsreasoning },
                            { title: 'Decision', content: decision },
                            { title: 'Conclusion', content: conclusion },
                        ].map((item, idx) => (
                            <div key={idx}>
                                <h4 className={`font-semibold mb-1 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{item.title}</h4>
                                <p className={`p-2 rounded ${theme === 'dark' ? 'bg-[#252830] text-gray-300' : 'bg-white text-gray-600'}`}>{item.content}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Card;